import { useState, useEffect, useRef } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from "@stripe/react-stripe-js";

// Calls
import {
  fetchSubscriptionPlansRequest,
  fetchPaymentMethodsRequest,
  createSetupIntentRequest,
  subscribeToPlanRequest,
  fetchActiveSubscriptionRequest
} from "../../../../api/calls/subscription.call";

// Actions
import { fetchActiveSubscription } from "../../../../redux/actions/business.actions";
import { fetchAuthenticatedUserData } from "../../../../redux/actions/app.actions";

// Billing UI
import {
  stripePromise,
  ELEMENTS_OPTIONS,
  StripeCardField,
  Spinner,
  InlineError,
  PageHead,
  SavedCardRow,
  money,
  fmtDate,
  fmtShort,
  fmtStamp,
  addDays,
  locationsLabel,
  confirmSubscriptionSecret
} from "./shared";
import { I } from "./icons";

// Utils
import { isEmpty } from "../../../../utils";

const POLL_ATTEMPTS = 12;
const POLL_INTERVAL_MS = 1250;
const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

/* Wait for the webhook to record the subscription locally. */
const pollForSubscription = async () => {
  for (let attempt = 0; attempt < POLL_ATTEMPTS; attempt++) {
    await sleep(POLL_INTERVAL_MS);
    const response = await fetchActiveSubscriptionRequest();
    const subscription = response.hasError
      ? null
      : response.activeSubscription;
    if (
      subscription &&
      ["active", "trialing"].includes(subscription.status)
    ) {
      return subscription;
    }
  }
  return null;
};

const OrderSummary = ({ plan }) => {
  const isTrial = plan.trialPeriodDays > 0;
  const trialEnd = addDays(new Date(), plan.trialPeriodDays || 0);
  const included = plan.subscriptionPlanFeatures.filter(f => f.supported);
  return (
    <aside className="co-summary">
      <div className="co-sum-card panel">
        <div className="co-sum-head">
          <div>
            <div className="co-sum-eyebrow">Order summary</div>
            <div className="co-sum-plan">{plan.name} plan</div>
          </div>
          <span className="badge badge-accent">Monthly</span>
        </div>
        <div className="co-sum-price">
          <span className="co-sum-amt">{money(plan.price.unitAmount)}</span>
          <span className="co-sum-per">/mo</span>
        </div>
        {plan.perBookingFee ? (
          <div className="co-sum-perbooking">
            + {money(plan.perBookingFee)} per completed booking
          </div>
        ) : null}

        <div className="co-recap">
          <div className="co-recap-loc">
            <I.MapPin width="14" height="14" />{" "}
            {locationsLabel(plan.allowedNumberOfBranches)}
          </div>
          {included.slice(0, 4).map(feature => (
            <div className="co-recap-row" key={feature.subscriptionFeature.title}>
              <I.Check width="12" height="12" />{" "}
              {feature.subscriptionFeature.title}
            </div>
          ))}
          {included.length > 4 && (
            <div className="co-recap-more">
              + {included.length - 4} more included
            </div>
          )}
        </div>

        {isTrial && (
          <div className="co-trial-box">
            <div className="co-trial-title">
              <I.Gift width="14" height="14" /> Free until {fmtDate(trialEnd)}
            </div>
            <div className="co-trial-text">
              You won't be charged until then. We'll email you before your
              trial ends. Cancel anytime.
            </div>
          </div>
        )}

        <div className="co-divider" />
        <div className="co-due-row">
          <span>Due today</span>
          <span className="co-due-amt">
            {isTrial ? "$0.00" : money(plan.price.unitAmount) + ".00"}
          </span>
        </div>
        <div className="co-due-note">
          {isTrial
            ? `Then ${money(plan.price.unitAmount)}/mo starting ${fmtShort(
                trialEnd
              )}`
            : "Billed monthly · renews automatically · cancel anytime"}
        </div>
      </div>
    </aside>
  );
};

const CheckoutInner = () => {
  const { planId } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const stripe = useStripe();
  const elements = useElements();
  const { activeSubscription } = useSelector(state => state.businessProfile);

  const [plan, setPlan] = useState(null);
  const [cards, setCards] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [sel, setSel] = useState("new");
  const [cardComplete, setCardComplete] = useState(false);
  const [phase, setPhase] = useState("form"); // form | processing | success
  const [procMsg, setProcMsg] = useState("Confirming your card…");
  const [error, setError] = useState(null);
  const [successSub, setSuccessSub] = useState(null);
  const mounted = useRef(true);

  useEffect(() => () => (mounted.current = false), []);

  useEffect(() => {
    const load = async () => {
      const [plansResponse, methodsResponse] = await Promise.all([
        fetchSubscriptionPlansRequest(),
        fetchPaymentMethodsRequest()
      ]);
      if (!mounted.current) return;
      if (plansResponse.hasError) {
        toast.error(plansResponse.error.message);
        history.replace("/subscription-plans");
        return;
      }
      const targetPlan = plansResponse.subscriptionPlans.find(
        p => String(p.id) === String(planId)
      );
      // Contact-only plans have no self-serve checkout.
      if (!targetPlan || targetPlan.contactOnly || !targetPlan.price) {
        history.replace("/subscription-plans");
        return;
      }
      setPlan(targetPlan);
      const paymentMethods = methodsResponse.hasError
        ? []
        : methodsResponse.paymentMethods;
      setCards(paymentMethods);
      const defaultCard =
        paymentMethods.find(pm => pm.isDefault) || paymentMethods[0];
      setSel(defaultCard ? defaultCard.id : "new");
      setLoaded(true);
    };
    load();
  }, [planId, history]);

  // Already subscribed → nothing to check out (plan changes go through the modal).
  useEffect(() => {
    if (phase === "form" && loaded && !isEmpty(activeSubscription)) {
      history.replace("/subscription-plans");
    }
  }, [activeSubscription, loaded, phase, history]);

  if (!loaded || !plan) {
    return (
      <div className="tf-billing" style={{ padding: 40, textAlign: "center" }}>
        <Spinner dark />
      </div>
    );
  }

  const isTrial = plan.trialPeriodDays > 0;
  const usingNew = sel === "new";
  const processing = phase === "processing";
  const canConfirm = stripe && (usingNew ? cardComplete : true);

  const fail = message => {
    if (!mounted.current) return;
    setPhase("form");
    setError(message);
  };

  const onConfirm = async () => {
    setError(null);
    setPhase("processing");
    setProcMsg("Confirming your card…");

    let paymentMethodId = sel;

    // New card → SetupIntent + confirmCardSetup gives us a payment method id.
    if (usingNew) {
      const setupIntentResponse = await createSetupIntentRequest();
      if (setupIntentResponse.hasError) {
        return fail(setupIntentResponse.error.message);
      }
      const setupResult = await stripe.confirmCardSetup(
        setupIntentResponse.clientSecret,
        { payment_method: { card: elements.getElement(CardElement) } }
      );
      if (setupResult.error) {
        return fail(setupResult.error.message);
      }
      paymentMethodId = setupResult.setupIntent.payment_method;
    }

    const subscribeResponse = await subscribeToPlanRequest(
      plan,
      paymentMethodId
    );
    if (subscribeResponse.hasError) {
      return fail(subscribeResponse.error.message);
    }

    // SCA / first charge confirmation when Stripe asks for it.
    if (subscribeResponse.clientSecret) {
      setProcMsg("Confirming your payment…");
      const confirmation = await confirmSubscriptionSecret(
        stripe,
        subscribeResponse.clientSecret,
        subscribeResponse.clientSecretType,
        paymentMethodId
      );
      if (!confirmation.ok) {
        return fail(confirmation.message);
      }
    }

    // Calm wait — the webhook records the subscription.
    setProcMsg("Activating your subscription…");
    const subscription = await pollForSubscription();
    if (!mounted.current) return;
    dispatch(fetchActiveSubscription());
    // Plan capabilities ride on dashboard_data — refresh or the menu keeps the
    // old plan's locks until a reload.
    dispatch(fetchAuthenticatedUserData());
    setSuccessSub(subscription);
    setPhase("success");
  };

  if (phase === "success") {
    return (
      <div className="tf-billing">
        <div className="co-success panel">
          <div className="co-success-ico">
            <I.Check width="34" height="34" />
          </div>
          <div className="co-success-title">
            {isTrial
              ? "Your free trial has started"
              : `You're subscribed to ${plan.name}`}
          </div>
          <div className="co-success-text">
            {successSub ? (
              isTrial ? (
                <>
                  Enjoy TruckFix free until{" "}
                  <b>{fmtStamp(successSub.currentPeriodEnd)}</b>. We'll email
                  you before your trial ends — cancel anytime before then and
                  you won't be charged.
                </>
              ) : (
                <>
                  Your <b>{plan.name}</b> plan is active. It renews monthly on{" "}
                  <b>{fmtStamp(successSub.currentPeriodEnd)}</b>.
                </>
              )
            ) : (
              <>
                Your payment is confirmed. We're finishing the activation —
                your subscription will appear on Business Profile shortly.
              </>
            )}
          </div>
          <div className="co-success-actions">
            <button
              className="btn btn-primary btn-lg"
              onClick={() => history.push("/")}
            >
              Go to Dashboard
            </button>
            <button
              className="btn btn-ghost btn-lg"
              onClick={() => history.push("/business-profile")}
            >
              View subscription
            </button>
          </div>
        </div>
      </div>
    );
  }

  const ctaLabel = isTrial
    ? "Start free trial"
    : `Subscribe — ${money(plan.price.unitAmount)}/mo`;

  return (
    <div className="tf-billing">
      <button
        className="link co-back"
        onClick={() => history.push("/subscription-plans")}
      >
        <I.Back /> Back to plans
      </button>
      <PageHead
        eyebrow="Checkout"
        title={isTrial ? "Start your free trial" : `Subscribe to ${plan.name}`}
      />

      <div className="co-grid">
        <OrderSummary plan={plan} />

        <div className="co-pay panel">
          <div className="co-pay-head">Payment method</div>

          <div className="co-pay-body">
            {cards.length > 0 && (
              <div className="co-cards">
                {cards.map(card => (
                  <SavedCardRow
                    key={card.id}
                    card={card}
                    selected={sel === card.id}
                    onSelect={() => !processing && setSel(card.id)}
                  />
                ))}
                <div
                  className={"radiorow" + (usingNew ? " sel" : "")}
                  onClick={() => !processing && setSel("new")}
                >
                  <span className="radio-mark">
                    <i />
                  </span>
                  <span className="card-brand">
                    <I.Plus width="14" height="14" />
                  </span>
                  <div style={{ fontSize: 13.5, fontWeight: 600 }}>
                    Use a new card
                  </div>
                </div>
              </div>
            )}

            {usingNew && (
              <div className={cards.length > 0 ? "co-newcard" : undefined}>
                <StripeCardField
                  invalid={!!error}
                  onChange={event => setCardComplete(event.complete)}
                />
              </div>
            )}

            {error && (
              <InlineError style={{ marginTop: 16 }}>{error}</InlineError>
            )}
          </div>

          <div className="co-pay-foot">
            <button
              className="btn btn-primary btn-lg btn-block"
              disabled={processing || !canConfirm}
              onClick={onConfirm}
            >
              {processing ? (
                <>
                  <Spinner /> {procMsg}
                </>
              ) : (
                ctaLabel
              )}
            </button>
            <div className="co-secure">
              <I.Lock width="12" height="12" /> Payments are processed securely
              by Stripe.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const CheckoutPage = () => (
  <Elements stripe={stripePromise} options={ELEMENTS_OPTIONS}>
    <CheckoutInner />
  </Elements>
);

export default CheckoutPage;
